<template>
    <v-container fluid>
        <v-row>

            <!--- CREATE NEW CLIENT -->
            <v-scale-transition>
                <v-col cols="12">
                    <title-card title="Create new API client" :help="createBoxHelp">

                        <v-container fluid class="pt-0">

                            <v-row dense>
                                <v-col cols="4" sm="12" md="3" >
                                    <v-text-field
                                            v-model="clientName"
                                            hide-details
                                            label="Please enter descriptive name">
                                    </v-text-field>
                                </v-col>
                                <v-col cols=3 md="3" sm="12">
                                    <v-btn @click="create" :loading="saving" small class="mt-5" color="primary">
                                        Create New Client
                                    </v-btn>
                                </v-col>
                                <v-col cols=3 sm="12" md="3" class="text-center">
                                    <v-btn v-if="docUrl" icon small class="mt-5" @click="openApiDocumentation">
                                        <v-icon class="mr-1">mdi-help-circle-outline</v-icon>
                                        API documentation
                                    </v-btn>
                                </v-col>
                            </v-row>

                        </v-container>
                    </title-card>
                </v-col>
            </v-scale-transition>

            <!-- CLIENT LIST -->
            <v-scale-transition>
                <v-col cols="12" v-show="clients.length > 0">

                    <title-card title="Active clients" :help="activeClientsBoxHelp">

                        <template v-slot:buttons>
                            <v-tooltip top>
                                <template v-slot:activator="{ on }">
                                    <v-btn @click="reload" v-on="on" icon>
                                        <v-icon>mdi-refresh</v-icon>
                                    </v-btn>
                                </template>
                                <span>Reload data</span>
                            </v-tooltip>
                        </template>

                        <v-alert type="warning" prominent class="body-2" icon="mdi-alert">
                            You must store secrets on your server and never expose them to the client side application.
                            Mobile or JavaScript SPA applications are unable to hold secrets securely!
                        </v-alert>

                        <v-scale-transition>
                            <v-alert v-show="created" type="warning" prominent class="body-2" icon="mdi-alert">
                                Copy API key to a safe place. This is the last time you will be able to see your secret
                                key!
                            </v-alert>
                        </v-scale-transition>

                        <v-data-table :loading="loading"
                                      :headers="headers"
                                      :items="clients"
                                      hide-default-footer
                        >
                            <template v-slot:body="{ items }">
                                <tbody>

                                <!-- ITEMS -->
                                <tr v-for="client in clients" :key="client.id">

                                    <td class="text-center">
                                        <v-chip>
                                            {{client.id}}
                                        </v-chip>
                                    </td>

                                    <td>
                                        {{client.name}}
                                    </td>

                                    <td v-if="client.secret">
                                        <v-tooltip color="primary" max-width="400" top>
                                            <template v-slot:activator="{ on }">
                                                <div v-on="on" @click="copy(client.secret)" class="cursor-pointer">
                                                    {{client.secret}}
                                                </div>
                                            </template>
                                            <span>
                                                Click to copy to your clip board
                                            </span>
                                        </v-tooltip>
                                    </td>
                                    <td v-else>
                                        <v-tooltip color="primary" max-width="400" top>
                                            <template v-slot:activator="{ on }">
                                            <span v-on="on" class="grey--text cursor-pointer">
                                            XXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXX
                                            </span>
                                            </template>
                                            <span>
                                            Key values hide after they issued. Only the creator can see the key value.
                                            If you have lost your key or don't know it anymore you will have to create
                                            a new client.
                                        </span>
                                        </v-tooltip>
                                    </td>

                                    <td>
                                        {{ toHumanFrom(client.created_at)}}
                                    </td>

                                    <td style="white-space: nowrap;" class="text-right">
                                        <v-tooltip color="primary" top>
                                            <template v-slot:activator="{ on }">
                                                <v-btn
                                                        @click="deleteClient(client)"
                                                        icon
                                                        text
                                                        class="ma-0"
                                                        v-on="on"
                                                        color="secondary">
                                                    <v-icon light small color="white">mdi-delete</v-icon>
                                                </v-btn>
                                            </template>
                                            <span>Delete client</span>
                                        </v-tooltip>
                                    </td>
                                </tr>
                                </tbody>
                            </template>

                        </v-data-table>

                    </title-card>

                </v-col>
            </v-scale-transition>

        </v-row>

        <confirmation-dialog v-model="confirmationDialogModel"
                             headline="Warning"
                             confirm-button-text="I know what I am doing"
                             cancel-button-text="ABORT"
                             delete
                             @selected="deleteConfirmed">
            This API key may be use. If you delete this API key your third party application(s) cannot
            connect to Sample Ninja API any more. If you want to rotate
            keys for security purposes then you should create a new key, updated any third party application(s)
            to use the new key and then delete the old key.
        </confirmation-dialog>

    </v-container>

</template>

<script>

    import TitleCard from "./TitleCard";

    import {timeDisplay} from "../mixins/timeDisplayMixin";
    import ConfirmationDialog from "./ConfirmationDialog";
    import {clipboardMixin} from "../mixins/clipboardMixin";

    export default {

        mixins: [timeDisplay, clipboardMixin],

        components: {
            TitleCard,
            ConfirmationDialog
        },

        props: {
            clients: {
                type: Array,
                required: true,
            },
            loading: {
                type: Boolean,
                required: false,
                default: false,
            },
            saving: {
                type: Boolean,
                required: false,
                default: false,
            },
            docUrl: {
                type: String,
                required: false,
                default: null
            },
            createBoxHelp: {
                type: String,
                required: false,
                default: null,
            },
            activeClientsBoxHelp: {
                type: String,
                required: false,
                default: null,
            }
        },

        data() {
            return {
                created: false,
                clientName: null,
                clientUnderDelete: null,
                confirmationDialogModel: false,
                tableSettings: {
                    itemsPerPage: 5,
                    sortBy: ['created_at'],
                    sortDesc: [true],
                },
                headers: [
                    {text: 'CLIENT ID', align: 'center', sortable: true, value: 'id', width: '120px'},
                    {text: 'NAME', align: 'left', sortable: true, value: 'name'},
                    {text: 'SECRET', sortable: false, value: 'secret', width: '180px'},
                    {text: 'CREATED AT', sortable: false, value: 'created_at', width: '200px'},
                    {text: 'ACTIONS', sortable: false, align: 'right', width: '40px'},
                ],
            }
        },

        methods: {

            create() {

                const payload = {
                    name: this.clientName,
                    redirect: 'https://www.sampleninja.io'
                };

                if (!this.clientName || this.clientName.length < 4) {
                    Bus.$emit('flash-message', {type: 'error', message: 'Client name must be at least 4 characters!'});
                    return;
                }

                this.$emit('create', payload);

                this.created = true;
                this.clientName = null;
            },

            deleteClient(client) {
                this.confirmationDialogModel = true;
                this.clientUnderDelete = client;
            },

            deleteConfirmed(confirmed) {

                if (confirmed) {
                    this.$emit('delete', this.clientUnderDelete.id)
                }

                this.clientUnderDelete = null;
            },

            reload() {
                this.created = false;
                this.$emit('reload');
            },

            copy(value) {
                this.copyToClipboard(value);
                Bus.$emit('flash-message', {message: 'Secret copied!'});
            },

            openApiDocumentation() {
                this.sendToUrl(this.docUrl, true);
            }
        }
    }
</script>

<style scoped>

</style>